import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, CornerDownLeft, Clock, Zap, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const RKEY = "eudr_recent_search";

export function CommandPalette() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(0);
  const seq = useRef(0);
  const recents = useMemo(() => (open ? JSON.parse(localStorage.getItem(`${RKEY}:${user?.id}`) || "[]") : []), [open, user?.id]);
  useEffect(() => {
    const k = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); } };
    const o = () => setOpen(true);
    window.addEventListener("keydown", k);
    window.addEventListener("open-search", o);
    return () => { window.removeEventListener("keydown", k); window.removeEventListener("open-search", o); };
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const n = ++seq.current;
    setBusy(true);
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => { if (n === seq.current) { setD(r.data); setIdx(0); } }).finally(() => n === seq.current && setBusy(false)), q ? 150 : 0);
    return () => clearTimeout(t);
  }, [q, open]);
  const flat = useMemo(() => {
    const groups = [];
    if (!q && recents.length) groups.push({ type: "Recent", icon: Clock, items: recents });
    if (d?.actions?.length) groups.push({ type: "Quick actions", icon: Zap, items: d.actions });
    (d?.groups || []).forEach((g) => groups.push(g));
    let i = 0;
    return groups.map((g) => ({ ...g, items: g.items.map((x) => ({ ...x, i: i++ })) }));
  }, [d, q, recents]);
  const items = flat.flatMap((g) => g.items);
  const go = (x) => {
    if (!x) return;
    if (x.sub !== undefined) localStorage.setItem(`${RKEY}:${user?.id}`, JSON.stringify([{ title: x.title, sub: x.sub, to: x.to }, ...recents.filter((r) => r.to !== x.to)].slice(0, 6)));
    setOpen(false); setQ(""); nav(x.to);
  };
  const key = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); go(items[idx]); }
  };
  useEffect(() => { document.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" }); }, [idx]);
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <DialogContent className="top-[18%] max-w-2xl translate-y-0 gap-0 overflow-hidden p-0" data-testid="command-palette">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-3 border-b border-slate-200 px-4">
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <Search className="h-4 w-4 text-slate-400" />}
          <input autoFocus data-testid="command-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={key} placeholder="Search materials, suppliers, customers, POs, billing docs, DDS, plants…" className="h-14 flex-1 bg-transparent text-[15px] outline-none placeholder:text-slate-400" />
          {d?.ms !== undefined && q && <span className="text-[10px] tabular-nums text-slate-400" data-testid="command-ms">{d.ms} ms</span>}
        </div>
        <div className="scroll-thin max-h-[420px] overflow-y-auto p-2" data-testid="command-results">
          {q && d && !d.groups.length && !d.actions.length && <p className="px-3 py-10 text-center text-sm text-slate-500" data-testid="command-empty">No results for '{q}'</p>}
          {flat.map((g) => (
            <div key={g.type} className="mb-1" data-testid={`command-group-${g.type.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{g.type}</p>
              {g.items.map((x) => (
                <button key={`${g.type}-${x.to}-${x.title}`} data-idx={x.i} data-testid={`command-item-${x.i}`} onMouseMove={() => setIdx(x.i)} onClick={() => go(x)}
                  className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors", idx === x.i ? "bg-[#D52B3B]/[0.07]" : "")}>
                  {g.icon && <g.icon className="h-3.5 w-3.5 text-slate-400" />}
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-slate-800">{x.title}</span>{x.sub && <span className="block truncate text-xs text-slate-500">{x.sub}</span>}</span>
                  {idx === x.i && <CornerDownLeft className="h-3.5 w-3.5 text-[#D52B3B]" />}
                </button>))}
            </div>))}
        </div>
        <div className="flex gap-4 border-t border-slate-100 bg-slate-50 px-4 py-2 text-[11px] text-slate-500"><span>↑↓ navigate</span><span>↵ open</span><span>esc close</span></div>
      </DialogContent>
    </Dialog>
  );
}
